import React, { FC, useCallback, useState } from "react";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Paper,
  Tooltip,
} from "@mui/material";
import { useDropzone, DropzoneOptions, FileRejection } from "react-dropzone";
import { useTranslation } from "react-i18next";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import styles from "@/components/forms/inputs/fileUpload/fileuploadSelection.module.css";
import classNames from "classnames";
import GenericButton from "@/components/forms/inputs/button/genericButton";

const DEFAULT_ACCEPT = {
  "application/msword": [".doc"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
    ".docx",
  ],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "application/pdf": [".pdf"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
    ".xlsx",
  ],
  "text/csv": [".csv"],
};

interface FileUploadSelectionProps {
  value: File[];
  onChange: (files: File[]) => void;
  multiple?: boolean;
  accept?: DropzoneOptions["accept"];
  maxSize?: number;
  disabled?: boolean;
}

const FileUploadSelection: FC<FileUploadSelectionProps> = ({
  value = [],
  onChange,
  multiple = true,
  accept = DEFAULT_ACCEPT,
  maxSize = 40_000_000,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const [rejectionErrors, setRejectionErrors] = useState<FileRejection[]>([]);

  const getErrorMessage = (errorCode: string) => {
    switch (errorCode) {
      case "file-too-large":
        return t("upload_file_size_too_large");
      case "file-invalid-type":
        return t("invalid_file_type");
      case "too-many-files":
        return t("too_many_files_error");
      default:
        return t("unknown_upload_error");
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        setRejectionErrors((prev) => [...fileRejections, ...prev]);
      }

      if (!multiple) {
        onChange([acceptedFiles[0]]);
        return;
      }

      const newFiles = [...value];

      acceptedFiles.forEach((file) => {
        const isDuplicate = newFiles.some(
          (f) => f.name === file.name && f.size === file.size,
        );

        if (!isDuplicate) {
          newFiles.push(file);
        }
      });

      onChange(newFiles);
    },
    [value, onChange, multiple],
  );
  const removeRejection = (index: number) => {
    setRejectionErrors((prev) => prev.filter((_, i) => i !== index));
  };

  const removeFile = (index: number) => {
    const newFiles = value.filter((_, i) => i !== index);
    onChange(newFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    accept,
    maxSize,
    disabled,
  });

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        {...getRootProps()}
        variant="outlined"
        className={classNames(styles.dropZone, {
          [styles.dropZone_dragActive]: isDragActive,
        })}
      >
        <input {...getInputProps()} />
        <CloudUploadIcon className={styles.cloudIcon} />
        <Typography variant="body1">
          {isDragActive
            ? t("upload_drop_files_here")
            : multiple
            ? t("upload_description")
            : t("upload_description_single_file")}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t("max_upload_filesize_40mb")}
        </Typography>
      </Paper>

      {rejectionErrors.length > 0 && (
        <Box>
          <List sx={{ mt: 1 }}>
            {rejectionErrors.map(({ file, errors }, index) => (
              <ListItem
                key={file.name}
                className={styles.rejection_error}
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={() => removeRejection(index)}
                    disabled={disabled}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  <ErrorOutlineIcon color="error" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ fontWeight: "bold" }}
                    >
                      {file.name}
                    </Typography>
                  }
                  secondary={errors.map((e) => (
                    <Typography
                      key={e.code}
                      variant="caption"
                      display="block"
                      color="error"
                    >
                      {getErrorMessage(e.code)}
                    </Typography>
                  ))}
                />
              </ListItem>
            ))}
          </List>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <GenericButton
              variant="contained"
              size="small"
              color="error"
              onClick={() => setRejectionErrors([])}
              disabled={disabled}
              //  sx={{ mt: 1, ml: 2 }}
            >
              {t("clear_all")}
            </GenericButton>
          </Box>
        </Box>
      )}

      {/* File List */}
      {value.length > 0 && (
        <List className={styles.fileList}>
          {value.map((file, index) => (
            <ListItem
              key={`${file.name}-${index}`}
              className={styles.fileListItem}
              secondaryAction={
                <Tooltip title={t("remove")}>
                  <GenericButton
                    variant="icon"
                    color="error"
                    onClick={() => removeFile(index)}
                    disabled={disabled}
                  >
                    <DeleteIcon />
                  </GenericButton>
                </Tooltip>
              }
            >
              <ListItemIcon>
                <InsertDriveFileIcon className={styles.fileListItem_icon} />
              </ListItemIcon>
              <ListItemText
                primary={file.name}
                secondary={formatBytes(file.size)}
                primaryTypographyProps={{ variant: "body2", noWrap: true }}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default FileUploadSelection;
